import { useCurrentUser } from '@/hooks/use-auth';
import { get } from '@/lib/api';
import { INCIDENT_STATUS_LABELS, formatRelativeTime, formatTimestamp } from '@/lib/constants';
import { queryKeys } from '@/lib/query-keys';
import { cn } from '@/lib/utils';
import { useQuery } from '@tanstack/react-query';
import type { AdminAuditEntry, AdminCustomerSummary, AdminIncidentSummary } from '@uncain/shared';
import { useState } from 'react';

export function AdminPage() {
	const { data: user } = useCurrentUser();
	const [activeTab, setActiveTab] = useState<'customers' | 'incidents' | 'audit'>('customers');

	if (user?.platformRole !== 'platform_admin') {
		return (
			<div className="rounded-lg border border-border bg-card p-8 text-center">
				<p className="text-destructive">Access denied. Platform admin required.</p>
			</div>
		);
	}

	return (
		<div className="space-y-6">
			<h1 className="text-2xl font-bold text-foreground">Platform Admin</h1>

			<div className="flex gap-2 border-b border-border">
				{(['customers', 'incidents', 'audit'] as const).map((tab) => (
					<button
						key={tab}
						type="button"
						onClick={() => setActiveTab(tab)}
						className={cn(
							'border-b-2 px-4 py-2 text-sm font-medium transition-colors',
							activeTab === tab
								? 'border-primary text-primary'
								: 'border-transparent text-muted-foreground hover:text-foreground',
						)}
					>
						{tab.charAt(0).toUpperCase() + tab.slice(1)}
					</button>
				))}
			</div>

			{activeTab === 'customers' && <CustomersList />}
			{activeTab === 'incidents' && <IncidentsList />}
			{activeTab === 'audit' && <AuditLog />}
		</div>
	);
}

function CustomersList() {
	const { data, isLoading } = useQuery<{ customers: AdminCustomerSummary[] }>({
		queryKey: queryKeys.admin.customers(),
		queryFn: () => get<{ customers: AdminCustomerSummary[] }>('/admin/customers'),
	});

	if (isLoading) return <div className="text-muted-foreground">Loading customers...</div>;

	const customers = data?.customers ?? [];

	return (
		<div className="space-y-3">
			{customers.length === 0 ? (
				<p className="text-sm text-muted-foreground">No customers.</p>
			) : (
				customers.map((c) => (
					<div key={c.orgId} className="rounded-lg border border-border bg-card p-4">
						<div className="flex items-center justify-between">
							<div className="flex items-center gap-3">
								{c.avatarUrl && (
									<img src={c.avatarUrl} alt={c.login} className="h-8 w-8 rounded-full" />
								)}
								<div>
									<p className="font-medium text-foreground">{c.login}</p>
									<p className="text-xs text-muted-foreground">Installation #{c.installationId}</p>
								</div>
							</div>
							<div className="text-right text-xs text-muted-foreground">
								<span className="capitalize">{c.status}</span>
							</div>
						</div>
						<p className="mt-2 text-xs text-muted-foreground">
							Installed {formatTimestamp(c.installedAt)}
						</p>
					</div>
				))
			)}
		</div>
	);
}

function IncidentsList() {
	const { data, isLoading } = useQuery<{ incidents: AdminIncidentSummary[] }>({
		queryKey: queryKeys.admin.incidents(),
		queryFn: () => get<{ incidents: AdminIncidentSummary[] }>('/admin/incidents'),
	});

	if (isLoading) return <div className="text-muted-foreground">Loading incidents...</div>;

	const incidents = data?.incidents ?? [];

	return (
		<div className="space-y-3">
			{incidents.length === 0 ? (
				<p className="text-sm text-muted-foreground">No incidents.</p>
			) : (
				incidents.map((inc) => (
					<div key={inc.id} className="rounded-lg border border-border bg-card p-4">
						<div className="flex items-center justify-between">
							<div>
								<p className="font-medium text-foreground">{inc.upstreamRepo}</p>
								<p className="text-xs text-muted-foreground">
									{inc.affectedCustomerCount} affected customers
								</p>
							</div>
							<span
								className={cn(
									'inline-flex rounded-full px-2 py-0.5 text-xs font-medium',
									inc.status === 'open' && 'bg-yellow-100 text-yellow-800',
									inc.status === 'resolved' && 'bg-green-100 text-green-800',
									inc.status === 'dismissed' && 'bg-gray-100 text-gray-800',
								)}
							>
								{INCIDENT_STATUS_LABELS[inc.status] ?? inc.status}
							</span>
						</div>
						<p className="mt-1 text-xs text-muted-foreground">
							Created {formatRelativeTime(inc.createdAt)}
						</p>
					</div>
				))
			)}
		</div>
	);
}

function AuditLog() {
	const { data, isLoading } = useQuery<{ entries: AdminAuditEntry[] }>({
		queryKey: queryKeys.admin.audit(),
		queryFn: () => get<{ entries: AdminAuditEntry[] }>('/admin/audit'),
	});

	if (isLoading) return <div className="text-muted-foreground">Loading audit log...</div>;

	const entries = data?.entries ?? [];

	return (
		<div className="space-y-2">
			{entries.length === 0 ? (
				<p className="text-sm text-muted-foreground">No audit entries.</p>
			) : (
				entries.map((entry) => (
					<div
						key={entry.id}
						className="flex items-center justify-between rounded border border-border px-3 py-2 text-sm"
					>
						<div className="flex items-center gap-3">
							<span className="font-mono text-xs text-muted-foreground">{entry.actorLogin}</span>
							<span className="text-foreground">{entry.action}</span>
							{entry.resourceType && (
								<span className="text-xs text-muted-foreground">on {entry.resourceType}</span>
							)}
						</div>
						<span className="text-xs text-muted-foreground">
							{formatRelativeTime(entry.createdAt)}
						</span>
					</div>
				))
			)}
		</div>
	);
}
